import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Menu,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Refresh,
  Search,
  Assignment,
  CalendarToday,
  Visibility,
  MoreVert,
  AssignmentInd
} from '@mui/icons-material';
import { getAllServiceBookings, getActiveServiceLocations, getServicePartners, assignServicePartner } from '../../../services/Api';
import { useNavigate } from 'react-router-dom';

const GetAllServiceBookings = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Search
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [locations, setLocations] = useState([]);

  // Action Menu State
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuSelectedBooking, setMenuSelectedBooking] = useState(null);

  // Assign Partner Modal State
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [partners, setPartners] = useState([]);
  const [loadingPartners, setLoadingPartners] = useState(false);
  const [selectedPartnerId, setSelectedPartnerId] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [partnerError, setPartnerError] = useState(null);

  // Toast feedback
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleMenuOpen = (event, booking) => {
    setAnchorEl(event.currentTarget);
    setMenuSelectedBooking(booking);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenAssignDialog = async (booking) => {
    setMenuSelectedBooking(booking);
    setSelectedPartnerId(booking.partnerId || '');
    setAssignDialogOpen(true);
    setPartnerError(null);

    try {
      setLoadingPartners(true);
      const res = await getServicePartners();
      if (res.success) {
        setPartners(res.data || []);
      } else {
        setPartnerError('Failed to load service partners');
      }
    } catch (err) {
      console.error('Error fetching service partners:', err);
      setPartnerError('Failed to load service partners. Please try again.');
    } finally {
      setLoadingPartners(false);
    }
  };

  const handleAssignSubmit = async () => {
    if (!selectedPartnerId) {
      setSnackbar({
        open: true,
        message: 'Please select a partner to assign.',
        severity: 'warning'
      });
      return;
    }

    try {
      setAssigning(true);
      const res = await assignServicePartner(menuSelectedBooking.id, selectedPartnerId);
      if (res.success) {
        setSnackbar({
          open: true,
          message: 'Partner assigned successfully.',
          severity: 'success'
        });
        setAssignDialogOpen(false);
        // Refresh bookings list to show updated partner assignment
        fetchBookings(page + 1, rowsPerPage, debouncedSearch, statusFilter, paymentStatusFilter, locationFilter);
      } else {
        setSnackbar({
          open: true,
          message: res.message || 'Failed to assign partner.',
          severity: 'error'
        });
      }
    } catch (err) {
      console.error('Error assigning partner:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Failed to assign partner. Please try again.',
        severity: 'error'
      });
    } finally {
      setAssigning(false);
    }
  };

  // Status options
  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'pending', label: 'Pending' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'processing', label: 'Processing' },
    { value: 'out_for_delivery', label: 'Out for Delivery' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const paymentStatusOptions = [
    { value: '', label: 'All Payment Status' },
    { value: 'pending', label: 'Pending' },
    { value: 'success', label: 'Success' },
    { value: 'failed', label: 'Failed' }
  ];

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const res = await getActiveServiceLocations();
        if (res.success) {
          setLocations(res.data || []);
        }
      } catch (err) {
        console.error('Failed to fetch locations', err);
      }
    };
    fetchLocations();
  }, []);

  const fetchBookings = async (page = 1, limit = 10, search = '', status = '', paymentStatus = '', locationId = '') => {
    try {
      setLoading(true);
      setError(null);

      const response = await getAllServiceBookings(page, limit, status, paymentStatus, search, locationId);

      if (response.success) {
        setBookings(response.data || []);
        setTotalRecords(response?.totalRecords || response?.totalItems || response?.totalCount || response?.totalDocs || response?.total || response?.data?.totalRecords || response?.data?.totalItems || response?.data?.totalCount || response?.data?.totalDocs || response?.data?.total || response?.data?.totalElements || response?.data?.length || response.totalRecords || 0 || 0);
      }
    } catch (err) {
      console.error('Error fetching service bookings:', err);
      setError(err.response?.data?.message || 'Failed to fetch service bookings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings(1, 10, debouncedSearch, statusFilter, paymentStatusFilter, locationFilter);
  }, [debouncedSearch, statusFilter, paymentStatusFilter, locationFilter]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchBookings(newPage + 1, rowsPerPage, debouncedSearch, statusFilter, paymentStatusFilter, locationFilter);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchBookings(1, newRowsPerPage, debouncedSearch, statusFilter, paymentStatusFilter, locationFilter);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleRefresh = () => {
    setSearchTerm('');
    setPage(0);
    setBookings([]);
    setStatusFilter('');
    setPaymentStatusFilter('');
    setLocationFilter('');
    fetchBookings(1, rowsPerPage, '', '', '', '');
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handlePaymentStatusFilterChange = (event) => {
    setPaymentStatusFilter(event.target.value);
    setPage(0);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'confirmed': return 'info';
      case 'processing': return 'secondary';
      case 'out_for_delivery': return 'primary';
      case 'delivered': return 'success';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getPaymentStatusColor = (paymentStatus) => {
    switch (paymentStatus) {
      case 'pending': return 'warning';
      case 'success': return 'success';
      case 'completed': return 'success';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Assignment sx={{ fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 300, color: 'text.primary' }}>
              Service Bookings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage and view all service bookings
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Filters Section */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'center' }, mb: 4, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search bookings..."
          size="small"
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{
            width: { xs: '100%', sm: 250 },
            '& .MuiOutlinedInput-root': {
              borderRadius: '20px',
              bgcolor: 'rgba(0,0,0,0.02)'
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 18, color: 'text.secondary' }} />
              </InputAdornment>
            )
          }}
        />

        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={handleStatusFilterChange}
          >
            {statusOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 } }}>
          <InputLabel>Payment Status</InputLabel>
          <Select
            value={paymentStatusFilter}
            label="Payment Status"
            onChange={handlePaymentStatusFilterChange}
          >
            {paymentStatusOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
          <InputLabel>Locations</InputLabel>
          <Select
            value={locationFilter}
            label="Locations"
            onChange={(e) => { setLocationFilter(e.target.value); setPage(0); }}
          >
            <MenuItem value="">All Locations</MenuItem>
            {locations.map((loc) => (
              <MenuItem key={loc.id} value={loc.id}>
                {loc.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <IconButton onClick={handleRefresh} disabled={loading} sx={{ color: 'primary.main' }}>
          {loading ? <CircularProgress size={24} color="inherit" /> : <Refresh />}
        </IconButton>
      </Box>

      {error && (
        <Box sx={{ mb: 4, p: 2, bgcolor: 'error.light', borderRadius: 1, color: 'error.dark' }}>
          <Typography>{error}</Typography>
        </Box>
      )}

      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'text.border', borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="service bookings table">
            <TableHead sx={{ backgroundColor: 'rgba(0,0,0,0.02)' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>S.No</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Booking ID</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Customer</TableCell>
                {/* <TableCell sx={{ fontWeight: 700 }}>Services</TableCell> */}
                <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Booking Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Payment Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Payment Method</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Partner</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Date & Time</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Last Updated</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && bookings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 10 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : bookings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 10 }}>
                    <Assignment sx={{ fontSize: 40, color: 'text.secondary', opacity: 0.3, mb: 1 }} />
                    <Typography color="text.secondary">
                      {searchTerm || statusFilter || paymentStatusFilter
                        ? 'No bookings found matching your criteria'
                        : 'No bookings found'
                      }
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                bookings.map((booking, index) => (
                  <TableRow key={booking.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {(page * rowsPerPage) + index + 1}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {booking.bookingNumber || `#${booking.id}`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {booking.user?.name || 'N/A'}
                        </Typography>
                      </Box>
                    </TableCell>
                    {/* <TableCell>
                      <Box>
                        {booking.items && booking.items.length > 0 ? (
                          booking.items.map((item, index) => (
                            <Box key={item.id} sx={{ mb: 0.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {item.serviceOptionName || 'N/A'}
                              </Typography>
                              {index < booking.items.length - 1 && <Box sx={{ mt: 1, mb: 0.5, height: 1, bgcolor: 'text.border', opacity: 0.3 }} />}
                            </Box>
                          ))
                        ) : (
                          <Typography variant="body2">No items</Typography>
                        )}
                      </Box>
                    </TableCell> */}

                    <TableCell>
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 500 }}>
                        ₹ {booking.totalAmount || '0.00'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={booking.bookingStatus || 'N/A'}
                        size="small"
                        color={getStatusColor(booking.bookingStatus)}
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={booking.paymentStatus || 'N/A'}
                        size="small"
                        color={getPaymentStatusColor(booking.paymentStatus)}
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={booking.paymentMethod || 'N/A'}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          textTransform: 'capitalize',
                          ...(booking.paymentMethod?.toLowerCase() === 'online' ? {
                            bgcolor: 'info.light', color: 'info.dark'
                          } : booking.paymentMethod?.toLowerCase() === 'cod' ? {
                            bgcolor: 'warning.light', color: 'warning.dark'
                          } : {
                            bgcolor: 'default'
                          })
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: booking.partnerId ? 'success.main' : 'error.main' }}>
                        {booking.partnerId ? 'Assigned' : 'Not Assigned'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CalendarToday sx={{ fontSize: 14 }} />
                          {booking.date ? new Date(booking.date).toLocaleDateString() : 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {booking.timeSlot || 'N/A'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {booking.updatedAt
                          ? new Date(booking.updatedAt).toLocaleDateString()
                          : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, booking)}
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            elevation: 3,
            sx: { minWidth: 150, borderRadius: 2, mt: 1 }
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={() => {
            handleMenuClose();
            if (menuSelectedBooking) navigate(`/bookings/service-bookings/view/${menuSelectedBooking.id}`);
          }}>
            <ListItemIcon>
              <Visibility fontSize="small" color="primary" />
            </ListItemIcon>
            View Details
          </MenuItem>
          <MenuItem onClick={() => {
            const booking = menuSelectedBooking;
            handleMenuClose();
            if (booking) handleOpenAssignDialog(booking);
          }}>
            <ListItemIcon>
              <AssignmentInd fontSize="small" color="secondary" />
            </ListItemIcon>
            Assign Partner
          </MenuItem>
        </Menu>

        {bookings.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={totalRecords}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Paper>

      {/* Assign Partner Dialog */}
      <Dialog
        open={assignDialogOpen}
        onClose={() => !assigning && setAssignDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            padding: 1,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: '50%',
            bgcolor: 'secondary.light',
            color: 'secondary.main'
          }}>
            <AssignmentInd />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Assign Partner
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Booking {menuSelectedBooking?.bookingNumber || `#${menuSelectedBooking?.id}`}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ py: 2 }}>
          {partnerError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {partnerError}
            </Alert>
          )}

          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Select a service partner to handle this booking. The partner will be notified of the assignment immediately.
            </Typography>

            {loadingPartners ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={36} color="secondary" />
              </Box>
            ) : partners.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                No active service partners found. Please make sure service partners are registered and active.
              </Alert>
            ) : (
              <FormControl fullWidth>
                <InputLabel id="select-partner-label">Service Partner</InputLabel>
                <Select
                  labelId="select-partner-label"
                  id="select-partner"
                  value={selectedPartnerId}
                  label="Service Partner"
                  onChange={(e) => setSelectedPartnerId(e.target.value)}
                  disabled={assigning}
                  sx={{ borderRadius: 2 }}
                >
                  {partners.map((partner) => (
                    <MenuItem key={partner.id} value={partner.id}>
                      {partner.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setAssignDialogOpen(false)}
            disabled={assigning}
            variant="text"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              color: 'text.secondary'
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAssignSubmit}
            disabled={assigning || loadingPartners || partners.length === 0}
            variant="contained"
            color="secondary"
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              boxShadow: 'none',
              '&:hover': {
                boxShadow: 'none'
              }
            }}
          >
            {assigning ? <CircularProgress size={20} color="inherit" /> : 'Assign Partner'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%', borderRadius: 2, boxShadow: 3 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GetAllServiceBookings;
